/**
 * Static registry of co-branding partners.
 *
 * The partner is activated globally via the `pay_form_filter` cookie, which is
 * set by `server/middleware/filterCookies.ts` when a request arrives with a
 * `?pay_form=<slug>` query (e.g. the redirect from Fonmoney:
 * `https://dapamas.com/store?token=...&pay_form=fonmoney`).
 *
 * This file holds the front-end-only branding data that the backend
 * `/api/external-partners` endpoint (see `composables/usePartners.ts`) does not
 * provide: legal imprint, support/contact details, return URL and color palette.
 *
 * To add a new partner: add an entry keyed by its `pay_form` slug and drop its
 * logo at `public/img/partners/<slug>.png`.
 */

export type PartnerContact = {
    email?: string
    phone?: string
    /** URL of the partner's customer support / help center. */
    supportUrl?: string
}

/**
 * Per-surface color tokens for fine-grained control of the co-branded chrome.
 * Every token is optional and falls back to the base palette (see
 * `resolvePartnerTheme`), so a partner only needs to override what differs.
 * All values are any valid CSS color (hex, rgb, etc.).
 */
export type PartnerTheme = {
    /** Top trust strip ("Compras a través de…" + back button). */
    topbarBg?: string
    topbarText?: string
    backButtonBg?: string
    backButtonText?: string
    /** Second navbar (categories / stores dropdowns). */
    subheaderBg?: string
    subheaderText?: string
    /** Co-branded footer. */
    footerBg?: string
    footerText?: string
    /** Secondary/dimmed footer text (taglines, imprint). */
    footerMuted?: string
    /** Footer links (email, phone, support). */
    footerLink?: string
}

export type PartnerConfig = {
    /** Must match the `pay_form` slug / `pay_form_filter` cookie value. */
    slug: string
    /** Display name used across the co-branded UI. */
    name: string
    /** Logo shown next to the Dapamas logo. Defaults to `/img/partners/<slug>.png`. */
    logo: string
    /** Alternative color logo */
    logoAlt?: string
    /** Destination of the "Volver a <Partner>" button. */
    returnUrl: string
    /** Brand palette applied as CSS variables while the partner is active. */
    colorPrimary: string
    colorSecondary: string
    /**
     * Text/foreground color used on top of partner-colored surfaces (top bar,
     * footer). Must contrast against `colorPrimary` — light palettes need a dark
     * value and vice versa.
     */
    colorText: string
    /** Partner contact/support shown in the co-branded footer. */
    contact: PartnerContact
    /**
     * Legal imprint line. The partner is the legal entity of the sale; Dapamas
     * is only the technical provider. Shown verbatim in the footer.
     */
    legalEntity: string
    /** Optional per-surface color overrides. Anything omitted uses the base palette. */
    theme?: PartnerTheme
}

export const PARTNERS: Record<string, PartnerConfig> = {
    fonmoney: {
        slug: "fonmoney",
        name: "Fonmoney",
        logo: "/img/partners/fonmoney.png",
        logoAlt: "/img/partners/fonmoney-alt.png",
        // TODO(cliente): confirmar URL exacta de retorno a Fonmoney.
        returnUrl: "https://www.fonmoney.com",
        // TODO(cliente): reemplazar por los hex exactos de la paleta Fonmoney.
        // Por ahora = paleta Dapamas por defecto (sin cambio visual hasta confirmar).
        colorPrimary: "#009EDA",
        colorSecondary: "#009EDA",
        // Texto oscuro: contrasta sobre el azul claro de la marca.
        colorText: "#000000",
        contact: {
            // TODO(cliente): datos reales de contacto/soporte de Fonmoney.
            email: "support@fonmoney.com",
            phone: "",
            supportUrl: "https://www.fonmoney.com/support",
        },
        // TODO(cliente): texto legal definitivo del imprint.
        legalEntity:
            "Fonmoney como entidad legal · Dapamas como proveedor técnico",
        // Knobs por superficie. Cualquiera que quites usa el fallback de la
        // paleta base (colorPrimary / colorText). Ajusta libremente.
        theme: {
            topbarBg: "#bee1fe",
            topbarText: "#000000",
            backButtonBg: "#000000",
            backButtonText: "#bee1fe",
            subheaderBg: "#ffffff",
            subheaderText: "#000000",
            footerBg: "#ffffff",
            footerText: "#000000",
            footerMuted: "rgba(0, 0, 0, 0.7)",
            footerLink: "#000000",
        },
    },
}

export function getPartner(slug?: string | null): PartnerConfig | null {
    if (!slug) return null
    return PARTNERS[slug] ?? null
}

/** Fully-resolved per-surface tokens: each `theme` override or its base fallback. */
export type ResolvedPartnerTheme = Required<PartnerTheme>

/**
 * Resolves the per-surface color tokens for a partner, filling any omitted
 * `theme` value from the base palette (`colorPrimary` / `colorText`). This is the
 * single place where the surface → base-color fallbacks are defined.
 */
export function resolvePartnerTheme(
    partner: PartnerConfig
): ResolvedPartnerTheme {
    const t = partner.theme ?? {}
    const onBrand = partner.colorText
    const brand = partner.colorPrimary
    return {
        topbarBg: t.topbarBg ?? brand,
        topbarText: t.topbarText ?? onBrand,
        backButtonBg: t.backButtonBg ?? onBrand,
        backButtonText: t.backButtonText ?? brand,
        subheaderBg: t.subheaderBg ?? brand,
        subheaderText: t.subheaderText ?? onBrand,
        footerBg: t.footerBg ?? brand,
        footerText: t.footerText ?? onBrand,
        footerMuted: t.footerMuted ?? onBrand,
        footerLink: t.footerLink ?? onBrand,
    }
}
